import SpinnerButton from 'common/components/SpinnerButton';
import { AppSyncFarm } from 'common/models/farm';
import { Nullable, Optional } from 'common/models/optional';
import { FarmUserInfo } from 'common/models/token';
import React from 'react';
import { useKey } from 'react-use';
import { FarmWithdrawEvent } from 'xstate/farmWithdrawEvent';
import {
  EventUpdateAccountInfoArgs,
  initialWithdrawContext,
  vaultWithdrawService,
  EventLiquidityPercentageChangeArgs,
} from 'xstate/vaultWithdrawMachine';
import { XStateUtils } from 'xstate/xStateUtils';
import PercentageSelector from '../PercentageSelector/PercentageSelector';
import { RemoteTxEvent } from 'xstate/txMachine';
import debounce from 'lodash/debounce';
import LiquidityModal from '../Modal/LiquidityModal';
import { FarmUtils } from 'utils/farmUtils';
import WithdrawingAmounts from './WithdrawingAmounts';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount } from 'wagmi';
import { VaultApi } from 'sdk/vaultApi';

type Props = {
  farm: Nullable<AppSyncFarm>;
  isShowing: boolean;
  toggle: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  farmUserInfo: Optional<FarmUserInfo>;
  farmUserInfoLoading: boolean;
  refetchFarmUserInfo: () => Promise<void>;
  updatePool: (poolId: number) => Promise<void>;
  type?: string;
};

const WithdrawLiquidityModalContainer: React.FC<Props> = (props) => {
  const { chainId } = useSelectedChain();
  const { address: accountAddress } = useAccount();

  const [isWithdrawing, setIsWithdrawing] = React.useState(
    initialWithdrawContext.isWithdrawing
  );

  const [amount, setAmount] = React.useState(initialWithdrawContext.amount);
  const [amountUsd, setAmountUsd] = React.useState(
    initialWithdrawContext.amountUsd
  );
  const [isLoadingData, setIsLoadingData] = React.useState(
    initialWithdrawContext.isLoadingData
  );
  const [inputError, setInputError] = React.useState(
    initialWithdrawContext.inputError
  );
  const [availableLP, setAvailableLP] = React.useState(
    initialWithdrawContext.availableLP
  );
  const [availableLPUSD, setAvailableLPUSD] = React.useState(
    initialWithdrawContext.availableLPUSD
  );

  const [innerLiquidityPercentage, setInnerLiquidityPercentage] =
    React.useState(initialWithdrawContext.liquidityPercentage);

  const baseToken = FarmUtils.getBaseToken(props.farm);
  const baseTokenDisplayName = baseToken?.displayName;

  const refreshData = async () => {
    if (props.farm && chainId && accountAddress) {
      await props.updatePool(props.farm.poolId);
      await props.refetchFarmUserInfo();
    }
  };
  const debouncedRefreshData = debounce(refreshData, 750);

  React.useEffect(() => {
    const subscription = vaultWithdrawService.subscribe((state) => {
      if (state.context.amount !== amount) {
        setAmount(state.context.amount);
      }

      if (state.context.amountUsd !== amountUsd) {
        setAmountUsd(state.context.amountUsd);
      }

      if (state.context.liquidityPercentage !== innerLiquidityPercentage) {
        setInnerLiquidityPercentage(state.context.liquidityPercentage);
      }

      if (state.context.inputError != inputError) {
        setInputError(state.context.inputError);
      }

      if (state.context.isLoadingData !== isLoadingData) {
        setIsLoadingData(state.context.isLoadingData);
      }

      if (state.context.isWithdrawing !== isWithdrawing) {
        setIsWithdrawing(state.context.isWithdrawing);
      }

      if (state.context.availableLP !== availableLP) {
        setAvailableLP(state.context.availableLP);
      }

      if (state.context.availableLPUSD !== availableLPUSD) {
        setAvailableLPUSD(state.context.availableLPUSD);
      }
    });

    vaultWithdrawService.onEvent((e) => {
      if (e.type === RemoteTxEvent.REMOTE_TX_SUCCESS) {
        // setAmount('0');
        // props.toggle();
        void debouncedRefreshData();
      }
    });

    return subscription.unsubscribe;
  }, [
    vaultWithdrawService,
    amount,
    amountUsd,
    availableLP,
    availableLPUSD,
    inputError,
    isLoadingData,
    isWithdrawing,
  ]);

  useKey('Escape', () => props.toggle());

  React.useEffect(() => {
    const args: EventLiquidityPercentageChangeArgs = {
      value: innerLiquidityPercentage,
    };
    vaultWithdrawService.send(
      FarmWithdrawEvent.LIQUIDITY_PERCENTAGE_CHANGE,
      XStateUtils.createEventPayload(args)
    );
  }, [innerLiquidityPercentage]);

  React.useEffect(() => {
    if (
      VaultApi.Vault.getInstance() != null &&
      accountAddress &&
      chainId &&
      props.farm &&
      props.farmUserInfo
    ) {
      const payload: EventUpdateAccountInfoArgs = {
        address: accountAddress,
        chainId: chainId,
        // TODO: we need to only send the fields we want, not these entire objects imo
        farm: props.farm,
        farmUserInfo: props.farmUserInfo,
      };
      vaultWithdrawService.send(
        FarmWithdrawEvent.UPDATE_ACCOUNT_INFO,
        XStateUtils.createEventPayload(payload)
      );
    }
  }, [chainId, accountAddress, props.farm, props.farmUserInfo]);

  const isWithdrawButtonDisabled =
    !amount ||
    amount === '0' ||
    isWithdrawing ||
    inputError != null ||
    isLoadingData;

  return (
    <LiquidityModal isShowing={props.isShowing} hide={props.toggle}>
      <div className="flex justify-center p-4 text-[20px] font-semibold border-b-2 border-gray-200">
        Withdraw
      </div>
      <div className="flex flex-col px-2 tablet:px-8 pb-6">
        <div className="grid">
          <div className="text-center primary-text-color m-7 font-medium">
            How much would you like to withdraw?
          </div>
          <div className="flex flex-col content-inner-bg-color rounded-[20px] p-5">
            <PercentageSelector
              value={innerLiquidityPercentage}
              onChange={setInnerLiquidityPercentage}
              availableAmount={`${(availableLPUSD / baseToken?.price)
                .toFixed(2)
                .toString()} ${baseTokenDisplayName}`}
              availableAmountLabel="Available LP:"
            />
            {inputError != null && <div className="error">{inputError}</div>}
            <WithdrawingAmounts
              farm={props.farm}
              farmUserInfo={props.farmUserInfo}
              availableLP={BigInt(availableLP || '0')}
              availableLPUSD={availableLPUSD}
              innerLiquidityPercentage={innerLiquidityPercentage}
              isLoading={props.farmUserInfoLoading}
              amount={amount}
              amountUSD={amountUsd}
              type={props.type}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-center gap-3 items-center h-24 box-footer pb-3">
        <button
          onClick={props.toggle}
          className="primary-text-color border-[1px] border-gray-200 font-semibold"
        >
          Cancel
        </button>
        <SpinnerButton
          className="primary-button-bg-color"
          isLoading={isWithdrawing || isLoadingData}
          disabled={isWithdrawButtonDisabled}
          onClick={() => vaultWithdrawService.send(FarmWithdrawEvent.WITHDRAW)}
        >
          Withdraw
        </SpinnerButton>
      </div>
    </LiquidityModal>
  );
};

export default WithdrawLiquidityModalContainer;
